import { useMemo, useCallback } from 'react';
import { useServices } from './use-services';

/**
 * Globaler Service-Lookup: serviceId → Anzeigename.
 * Spec 409: bevorzugt das admin-seitige `displayName` (Facade liefert es seit #338),
 * faellt auf `description` zurueck (Services tragen kein `name`-Feld), dann auf die
 * serviceId. Der gespeicherte Wert bleibt ungetrimmt — Trimmen ist Sache der Zelle.
 * Cached ueber React Query (staleTime aus useServices).
 */
/** First non-blank value (trim-checked) — keeps the raw value, trimming is the cell's job. */
function firstNonBlank(...values: (string | undefined)[]): string | undefined {
  return values.find((v) => v != null && v.trim() !== '');
}

export function useServiceMap() {
  const { data } = useServices({ limit: 200 });

  const serviceMap = useMemo(() => {
    const m = new Map<string, string>();
    data?.data.forEach((service) => {
      // Full fallback chain in the lookup layer: displayName → description → serviceId.
      // The map value is therefore never blank (a nameless service maps to its id).
      const label =
        firstNonBlank(service.displayName, service.description) ?? service.serviceId;
      m.set(service.serviceId, label);
    });
    return m;
  }, [data]);

  const resolveServiceName = useCallback(
    (id: string) => serviceMap.get(id) ?? id,
    [serviceMap],
  );

  // Explicit, intention-revealing alias for filter chip/option labels (Spec 409, Phase 5).
  // Same display-chain semantics as resolveServiceName; named for the filter use case so
  // a consumer reads "give me the label for this serviceId" rather than reusing the cell helper.
  const resolveServiceLabel = resolveServiceName;

  return {
    serviceMap,
    resolveServiceName,
    resolveServiceLabel,
    services: data?.data ?? [],
  };
}
